import {
  createContext,
  useRef,
  useReducer,
  useEffect,
  useState,
  useContext,
  ReactNode,
} from 'react';
import classNames from 'classnames';

import type {
  VideoPlayerManagerContextValue,
  PlayerID,
  SourceContainerRef,
} from './types';
import { PopOutPlayerWrapper } from './components/pop-out-player-wrapper/pop-out-player-wrapper';
import {
  playerExists,
  videoIsPopped,
  videosStateReducer,
} from './helpers/state.helpers';
import {
  createVideoPlayer,
  moveVideoToOriginalContainer,
  moveVideoToPopOut,
} from './helpers/dom.helpers';
import {
  defaultContextValue,
  initialVideoState,
} from './pop-out-video.constants';

import floatingStyles from './pop-out-floating-container.module.scss';
import {
  CloseFullscreenButtonClicked,
  PlaybackEnded,
  PopOutButtonClicked,
} from './components/video-player/video-player.constants';
import { VideoJSPlayer } from 'video.js';
import { PlayStates, VisibilityStates } from '../pop-out-media-manager/types';
import { OverlayWrapper } from '../../layouts/overlay-manager/components/overlay-wrapper/overlay-wrapper';
import { indexNames } from '../../layouts/overlay-manager/overlay-manager.constants';
import type { OverlayManagerContextValue } from '../../layouts/overlay-manager/types';
import { OverlayManagerContext } from '../../layouts/overlay-manager/overlay-manager';
import {
  isPaused,
  popOutGoFullScreen,
  currentVideoCanBeUnPopped,
  inlinePlayerVisibilityState,
  pauseVideo,
  togglePlayState,
  handleVideoSeekShortcuts,
} from './video-player-manager.helpers';
import { handleCommonMediaShortcuts } from '../pop-out-media-manager/pop-out-media-manager.helpers';

export const VideoPlayerManagerContext =
  createContext<VideoPlayerManagerContextValue>(defaultContextValue);

type VideoPlayerManagerProviderProps = {
  children: ReactNode;
};

export const VideoPlayerManagerProvider = ({
  children,
}: VideoPlayerManagerProviderProps) => {
  const [shouldPauseAfterUnPop, setShouldPauseAfterUnPop] = useState(false);
  const [playerMap, dispatchVideoAction] = useReducer(
    videosStateReducer,
    initialVideoState,
  );
  const { updateDimensions, updateVisibility } =
    useContext<OverlayManagerContextValue>(OverlayManagerContext);
  const popOutRef = useRef<HTMLDivElement | null>(null);
  const popOutContainerRef = useRef<HTMLDivElement | null>(null);

  const overlayConfig = {
    componentName: VideoPlayerManagerProvider.displayName,
  } as const;

  useEffect(() => {
    /**
     * This effect will handle the situation where we have a video playing
     * in the pop out and we select another video to play. We check if the
     * last played video "Active Video" is the same as what is in the pop
     * out and if they differ we need to dismiss the pop out.
     */
    const haveBothIds =
      playerMap.popOutState.id && playerMap.activeVideoState.id;
    const idsMatch = playerMap.popOutState.id === playerMap.activeVideoState.id;

    if (!haveBothIds || idsMatch) {
      return;
    }

    unPopVideo(playerMap.popOutState.id, true);
  }, [playerMap.activeVideoState.id, playerMap.popOutState.id]);

  useEffect(() => {
    const popCurrentVideo = () => {
      if (!playerMap.activeVideoState.id) {
        return;
      }
      popVideo(playerMap.activeVideoState.id);
    };

    const closeCurrentFullscreen = () => {
      const currentId = playerMap.activeVideoState.id;
      if (!currentId) {
        return;
      }
      const controller = playerMap[currentId].controller;
      if (!controller) {
        return;
      }
      controller.exitFullscreen();
    };

    const videoEnded = e => {
      const videoThatEnded = e.target.player.tech().src();
      const currentVideo = playerMap.activeVideoState.id;
      if (videoThatEnded === currentVideo) {
        unPopVideo(currentVideo);
      }
    };

    for (const key in playerMap) {
      if (playerMap[key]?.controller) {
        playerMap[key].controller.on(PopOutButtonClicked, popCurrentVideo);
        (playerMap[key].controller as VideoJSPlayer).on(
          PlaybackEnded,
          videoEnded,
        );
        (playerMap[key].controller as VideoJSPlayer).on(
          CloseFullscreenButtonClicked,
          closeCurrentFullscreen,
        );
      }
    }

    return () => {
      for (const key in playerMap) {
        if (playerMap[key]?.controller) {
          playerMap[key].controller.off(PopOutButtonClicked, popCurrentVideo);
          (playerMap[key].controller as VideoJSPlayer).off(
            CloseFullscreenButtonClicked,
            closeCurrentFullscreen,
          );

          (playerMap[key].controller as VideoJSPlayer).off(
            PlaybackEnded,
            videoEnded,
          );
        }
      }
    };
  }, [playerMap]);

  useEffect(() => {
    const keyDownHandler = (evt: KeyboardEvent) => {
      const activeVideo = playerMap.activeVideoState.id;

      if (!activeVideo) {
        return;
      }
      const playerController = playerMap[activeVideo].controller;
      const didPerformCommonAction = handleCommonMediaShortcuts(
        evt,
        playerController,
        () => unPopVideo(activeVideo, true),
      );

      if (didPerformCommonAction) {
        return;
      }

      handleVideoSeekShortcuts(evt, playerController);
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => document.removeEventListener('keydown', keyDownHandler);
  }, [playerMap]);

  useEffect(() => {
    updateVisibility({
      ...overlayConfig,
      visibility: playerMap.popOutState.visibilityState,
    });
  }, [playerMap.popOutState.visibilityState]);

  const addInlinePlayer = (container: SourceContainerRef, src: string) => {
    const playerId = src;

    if (playerExists(playerMap, playerId)) {
      dispatchVideoAction({
        type: 'UPDATE_PLAYER_SOURCE_CONTAINER',
        payload: {
          sourceContainer: container,
          playerId,
        },
      });
      return;
    }

    const onPlayerReady = controller => {
      dispatchVideoAction({
        type: 'SET_CONTROLLER_INSTANCE',
        payload: {
          playerId,
          controller,
        },
      });
    };

    const onPlay = () => {
      dispatchVideoAction({
        type: 'SET_ACTIVE_VIDEO_STATE',
        payload: {
          id: src,
          playState: PlayStates.Playing,
        },
      });
    };

    const onPause = () => {
      dispatchVideoAction({
        type: 'SET_ACTIVE_VIDEO_PLAY_STATE',
        payload: {
          id: src,
          playState: PlayStates.Paused,
        },
      });
    };

    const newPlayer = createVideoPlayer(src, onPlayerReady, onPlay, onPause);

    dispatchVideoAction({
      type: 'ADD_VIDEO_PLAYER',
      payload: {
        player: newPlayer,
        sourceContainer: container,
        sourceContainerVisibilityState: VisibilityStates.Visible,
        playerId,
      },
    });

    if (container.current) {
      container.current.appendChild(newPlayer);
    }
  };

  const popVideo = (playerId: PlayerID) => {
    if (
      !playerExists(playerMap, playerId) ||
      videoIsPopped(playerMap, playerId)
    ) {
      return;
    }

    dispatchVideoAction({
      type: 'SET_INLINE_PLAYER_VISIBILITY_STATE',
      payload: {
        id: playerId,
        state: VisibilityStates.AnimatingOut,
      },
    });

    const onTransitionEnd = () => {
      dispatchVideoAction({
        type: 'POP_VIDEO',
        payload: playerId,
      });
      updateVisibility({
        ...overlayConfig,
        visibility: VisibilityStates.Visible,
      });

      const sourceRef = playerMap[playerId].sourceContainer.current;

      if (popOutRef.current && sourceRef) {
        moveVideoToPopOut(
          playerMap[playerId].player,
          popOutRef.current,
          sourceRef,
        );
      }

      if (sourceRef) {
        sourceRef.removeEventListener('transitionend', onTransitionEnd);
      }
    };

    if (!playerMap[playerId].sourceContainer.current) {
      return;
    }

    playerMap[playerId].sourceContainer.current.addEventListener(
      'transitionend',
      onTransitionEnd,
    );
  };

  const unPopVideo = (playerId: PlayerID | null, pauseAfter = false) => {
    if (!playerId || !videoIsPopped(playerMap, playerId)) {
      return;
    }
    setShouldPauseAfterUnPop(pauseAfter);
    updateVisibility({
      ...overlayConfig,
      visibility: VisibilityStates.Hidden,
    });
  };

  const removeInlinePlayer = (playerId: PlayerID) => {
    dispatchVideoAction({
      type: 'REMOVE_VIDEO_PLAYER',
      payload: playerId,
    });
  };

  const handleOverlayTransitionEnd = visibility => {
    const poppedId = playerMap.popOutState.id;
    if (visibility === VisibilityStates.Hidden) {
      if (!poppedId) {
        return;
      }

      if (playerMap[poppedId].sourceContainer.current) {
        moveVideoToOriginalContainer(
          playerMap[poppedId].player,
          playerMap[poppedId].sourceContainer.current,
        );
      } else if (popOutRef.current) {
        // destroy the video player if we cant send it back home!
        popOutRef.current.innerHTML = '';
      }

      dispatchVideoAction({
        type: 'UN_POP_VIDEO',
        payload: poppedId as PlayerID,
      });
      if (shouldPauseAfterUnPop) {
        pauseVideo(playerMap, poppedId);
      }
    }

    updateDimensions(overlayConfig);
  };

  const setNoActiveVideo = () => {
    dispatchVideoAction({
      type: 'SET_ACTIVE_VIDEO_STATE',
      payload: {
        id: null,
        playState: PlayStates.NoPlayer,
      },
    });
  };

  const showPopOut =
    playerMap.popOutState.visibilityState === VisibilityStates.Visible;

  return (
    <VideoPlayerManagerContext.Provider
      value={{
        popVideo,
        unPopVideo,
        setNoActiveVideo,
        addInlinePlayer,
        removeInlinePlayer,
        isPaused: id => isPaused(playerMap, id),
        popOutState: () => playerMap.popOutState,
        isPopped: id => videoIsPopped(playerMap, id),
        getCurrentlyActive: () => playerMap.activeVideoState,
        dismiss: () => unPopVideo(playerMap.popOutState.id, true),
        pauseVideo: () => pauseVideo(playerMap, playerMap.activeVideoState.id),
        inlinePlayerVisibilityState: id =>
          inlinePlayerVisibilityState(playerMap, id),
        popOutVisibilityState: playerMap.popOutState.visibilityState,
      }}
    >
      {children}
      <OverlayWrapper
        onTransitionEnd={handleOverlayTransitionEnd}
        {...overlayConfig}
        indexName={indexNames.popOutVideoPlayer}
      >
        <div
          ref={popOutContainerRef}
          className={classNames(
            floatingStyles['pop-out-floating-container'],
            showPopOut && floatingStyles['pop-out-floating-container--active'],
          )}
        >
          <div
            className={
              floatingStyles['pop-out-floating-container__video-container']
            }
          >
            <PopOutPlayerWrapper
              ref={popOutRef}
              showPopOutButton={currentVideoCanBeUnPopped(playerMap)}
              isPlaying={
                playerMap.activeVideoState.playState === PlayStates.Playing
              }
              onClosePopOutClick={() => {
                if (!playerMap.activeVideoState.id) {
                  return;
                }
                unPopVideo(playerMap.activeVideoState.id, false);
              }}
              onCloseClick={() => {
                if (!playerMap.activeVideoState.id) {
                  return;
                }
                unPopVideo(playerMap.activeVideoState.id, true);
              }}
              onFullScreenClick={() => {
                popOutGoFullScreen(playerMap);
              }}
              onOverlayClick={() => {
                if (!playerMap.activeVideoState.id) {
                  return;
                }
                togglePlayState(playerMap, playerMap.activeVideoState.id);
              }}
            />
          </div>
        </div>
      </OverlayWrapper>
    </VideoPlayerManagerContext.Provider>
  );
};

VideoPlayerManagerProvider.displayName = 'VideoPlayerManagerProvider';
